import styled from "@emotion/styled";
import dayjs from "dayjs";

import { type InvoicesResponse } from "@skilitics/billing-api-client/definition";
import {
  Cell,
  Chip,
  classNames,
  FONT_WEIGHT_MEDIUM,
  Icon,
  IconButton,
  PAGE_SIDE_PADDING,
  Table,
  TableBody,
  TableHead,
  TableRow,
} from "@skilitics/designer-ui-components";
import { useFormatMessage } from "@skilitics/localization";

import { FREEDOM_DATE_FORMAT } from "../utils";
import { invoicesFormatStrings } from "./formatStrings";
import { invoicesColumns } from "./invoicesColumns";
import { InvoicesTableLoadingRows } from "./InvoicesTableLoadingRows";

type Props = {
  invoices: InvoicesResponse["items"];
  isLoading?: boolean;
};

export const InvoicesTable = ({ invoices, isLoading }: Props) => {
  const formatMessage = useFormatMessage(invoicesFormatStrings);
  return (
    <StyledTable stickyHeader size="small">
      <StyledTableHead>
        <TableRow>
          {invoicesColumns.map((column) => (
            <Cell key={column.id} align={column.align} style={{ minWidth: column.minWidth, width: column.width }}>
              {formatMessage(column.id)}
            </Cell>
          ))}
        </TableRow>
      </StyledTableHead>
      <TableBody>
        {isLoading ? (
          <InvoicesTableLoadingRows />
        ) : !invoices.length ? (
          <TableRow>
            <Cell colSpan={invoicesColumns.length}>
              <NoneFoundMessage>{formatMessage("noInvoicesMessage")}</NoneFoundMessage>
            </Cell>
          </TableRow>
        ) : (
          invoices.map((invoice) => (
            <TableRow key={invoice.id}>
              <Cell align={invoicesColumns[0].align}>{dayjs(invoice.created * 1000).format(FREEDOM_DATE_FORMAT)}</Cell>
              <Cell align={invoicesColumns[1].align}>
                {!!invoice.due && dayjs(invoice.due * 1000).format(FREEDOM_DATE_FORMAT)}
              </Cell>
              <Cell align={invoicesColumns[2].align}>
                <InvoiceStatusChip status={invoice.status} />
              </Cell>
              <Cell align={invoicesColumns[3].align}>{invoice.amount}</Cell>
              <Cell align={invoicesColumns[4].align}>
                {invoice.pdf_url && (
                  /* @ts-ignore-error */
                  <StyledLinkIconButton href={invoice.pdf_url} download>
                    <Icon icon="download" />
                  </StyledLinkIconButton>
                )}
              </Cell>
            </TableRow>
          ))
        )}
      </TableBody>
    </StyledTable>
  );
};

const StyledTable = styled(Table)`
  tr {
    td:first-of-type,
    th:first-of-type {
      padding-left: ${PAGE_SIDE_PADDING / 2}px;
    }

    td:last-of-type,
    th:last-of-type {
      padding-right: ${PAGE_SIDE_PADDING / 2}px;
    }

    &:last-of-type td {
      border-bottom: none;
    }
  }
`;

const StyledTableHead = styled(TableHead)`
  ${Cell} {
    font-size: 0.75rem;
    background: ${({ theme }) => theme.surfaceVariant};
  }
`;

const NoneFoundMessage = styled.div`
  text-align: center;
  padding: 16px;
  color: ${({ theme }) => theme.generalTextIconVariant};
`;

const StyledLinkIconButton = styled(IconButton)`
  font-size: 18px;
`;

const StyledChip = styled(Chip)`
  text-transform: uppercase;
  font-weight: ${FONT_WEIGHT_MEDIUM};
  &.paid {
    background: ${({ theme }) => theme.tertiaryContainer};
    color: ${({ theme }) => theme.tertiary};
  }
  &.open {
    background: ${({ theme }) => theme.warningContainer};
    color: ${({ theme }) => theme.onWarningContainer};
  }
  &.void {
    background: ${({ theme }) => theme.surfaceVariant};
  }
`;

const InvoiceStatusChip = ({ status }: { status: string }) => {
  return <StyledChip label={status} className={classNames(status)} />;
};
